import React from "react";
import { GlobeAltIcon } from "@heroicons/react/20/solid";
import type { UsageResponse } from "../types";
import { PageLayout, PageContent, PageHeader, Callout } from "../../../ui";
import { SelectedMonthCard } from "./SelectedMonthCard";
import { ProjectedUsageCard } from "./ProjectedUsageCard";
import { MonthComparisonCard } from "./MonthComparisonCard";
import { DailyCreditsChart } from "./DailyCreditsChart";
import { UsageByModelTable } from "./UsageByModelTable";
import { PromptCachingCard } from "./PromptCachingCard";
import { UsageLimitsSection } from "./UsageLimitsSection";
import { MonthPicker } from "./MonthPicker";
import { currentUtcMonth, formatMonthLong } from "../utils/months";
import { REGIONS } from "../../../lib/regions";

interface UsageViewProps {
  usageData: UsageResponse;
  errorMessage?: string | null;
}

/** Map a region id ("us") to its display label ("US"); fall back to uppercase. */
function regionLabel(id: string): string {
  return REGIONS.find((r) => r.id === id)?.label ?? id.toUpperCase();
}

export function UsageView({ usageData, errorMessage }: UsageViewProps) {
  const { usage } = usageData;
  const isCurrentMonth = usage.selected_month === currentUtcMonth();
  const degradedRegions = usage.degraded_regions ?? [];
  const includedRegions = usage.included_regions ?? [];
  const isGlobalTotal = includedRegions.length > 1;
  const gridCols = isCurrentMonth
    ? "grid-cols-1 md:grid-cols-3"
    : "grid-cols-1 md:grid-cols-2";

  return (
    <PageLayout>
      <PageContent maxWidth="5xl">
        <PageHeader title="Usage & Billing" badge={<MonthPicker />} />

        {isGlobalTotal && (
          <p className="mb-6 -mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
            <GlobeAltIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            Combined across all regions ({includedRegions.map(regionLabel).join(" and ")}).
          </p>
        )}

        {errorMessage && (
          <Callout className="mb-6 border-destructive/30 bg-destructive/10 text-destructive">
            {errorMessage}
          </Callout>
        )}

        {degradedRegions.length > 0 && (
          <Callout className="mb-6 border-warning/30 bg-warning/15 text-warning-foreground">
            {degradedRegions.map(regionLabel).join(" and ")} usage couldn't be
            loaded — combined totals may be incomplete.
          </Callout>
        )}

        <div className={`grid ${gridCols} gap-6 mb-8`}>
          <SelectedMonthCard
            selectedMonth={usage.selected_month}
            rolloverIn={usage.rollover_credits_in}
            available={usage.available_credits}
            consumed={usage.total_credits_consumed}
            remaining={usage.credits_remaining}
          />
          {isCurrentMonth && (
            <ProjectedUsageCard
              selectedMonth={usage.selected_month}
              consumed={usage.total_credits_consumed}
              // Project against the available pool (allowance + rollover), so a
              // month covered by rolled-over credit isn't flagged as overage.
              allowance={usage.available_credits}
            />
          )}
          <MonthComparisonCard
            consumedThisPeriod={usage.total_credits_consumed}
            consumedPreviousPeriod={usage.previous_month_consumed_to_same_day}
            selectedMonth={usage.selected_month}
            previousMonth={usage.previous_month}
            previousMonthFullTotal={usage.previous_month_consumed}
            isCurrentMonth={isCurrentMonth}
          />
        </div>

        <div className="mb-4">
          <h2 className="text-xl font-semibold text-foreground">
            Daily Credit Consumption — {formatMonthLong(usage.selected_month)}
          </h2>
          {isCurrentMonth && (
            <p className="text-xs text-muted-foreground mt-1">
              As of today ({new Date().toLocaleDateString(undefined, {
                day: "numeric",
                month: "short",
              })})
            </p>
          )}
        </div>
        <div className="mb-8">
          <DailyCreditsChart
            selectedMonth={usage.selected_month}
            dailyConsumption={usage.daily_consumption}
            cores={Object.keys(usage.usage_by_model_core).sort()}
          />
        </div>

        <h2 className="text-xl font-semibold mb-4 text-foreground">
          Trace Caching — {formatMonthLong(usage.selected_month)}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <PromptCachingCard
            caching={usage.caching}
            selectedMonth={usage.selected_month}
          />
        </div>

        <h2 className="text-xl font-semibold mb-4 text-foreground">
          Usage by Model — {formatMonthLong(usage.selected_month)}
        </h2>
        <UsageByModelTable
          usageByModelCore={usage.usage_by_model_core}
          caching={usage.caching}
        />

        <UsageLimitsSection />
      </PageContent>
    </PageLayout>
  );
}
